import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useCalculos } from "@/hooks/useCalculos";
import { ProgressaoRegimeResult } from "@/types/calculos";
import { addToHistory, useCalculoPrefill } from "@/hooks/useCalculoHistory";

export const ProgressaoRegime = () => {
  const [penaTotal, setPenaTotal] = useState("");
  const [tipoRegime, setTipoRegime] = useState<'fechado' | 'semiaberto' | 'aberto'>('fechado');
  const [crimeHediondo, setCrimeHediondo] = useState(false);
  const [reincidente, setReincidente] = useState(false);
  const [resultado, setResultado] = useState<ProgressaoRegimeResult | null>(null);
  
  const { calcularProgressaoRegime, loading } = useCalculos();

  useCalculoPrefill("progressao", (v) => {
    setPenaTotal(v.penaTotal ?? "");
    setTipoRegime(v.tipoRegime ?? "fechado");
    setCrimeHediondo(v.crimeHediondo ?? false);
    setReincidente(v.reincidente ?? false);
    setResultado(null);
  });

  const handleCalcular = async () => {
    if (!penaTotal) {
      return;
    }

    try {
      const result = await calcularProgressaoRegime({
        penaTotal: parseFloat(penaTotal),
        tipoRegime,
        crimeHediondo,
        reincidente
      });
      setResultado(result);
      addToHistory({
        type: "progressao",
        label: "Progressão de Regime",
        values: { penaTotal, tipoRegime, crimeHediondo, reincidente },
        summary: `Pena ${penaTotal} meses • ${result.percentualCumprido}% • ${formatMeses(result.tempoNecessario)}`,
      });
    } catch (error) {
      console.error("Erro no cálculo:", error);
    }
  };

  const formatMeses = (meses: number) => {
    const anos = Math.floor(meses / 12);
    const mesesRestantes = Math.round(meses % 12);
    if (anos > 0) {
      return `${anos} ano${anos > 1 ? 's' : ''} e ${mesesRestantes} mês${mesesRestantes !== 1 ? 'es' : ''}`;
    }
    return `${mesesRestantes} mês${mesesRestantes !== 1 ? 'es' : ''}`;
  };

  const getPercentualTexto = () => {
    if (crimeHediondo && reincidente) return "3/5 (60%)";
    if (crimeHediondo && !reincidente) return "2/5 (40%)";
    return "1/6 (16,67%)";
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Progressão de Regime</CardTitle>
        <p className="text-sm text-muted-foreground">
          Cálculo do tempo necessário para progressão conforme Lei de Execução Penal
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="pena-total">Pena Total (meses)</Label>
            <Input
              id="pena-total"
              type="number"
              placeholder="60"
              value={penaTotal}
              onChange={(e) => setPenaTotal(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="regime">Regime Atual</Label>
            <Select value={tipoRegime} onValueChange={(value: any) => setTipoRegime(value)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="fechado">Fechado</SelectItem>
                <SelectItem value="semiaberto">Semiaberto</SelectItem>
                <SelectItem value="aberto">Aberto</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-center space-x-2">
            <Checkbox 
              id="hediondo" 
              checked={crimeHediondo}
              onCheckedChange={(checked) => setCrimeHediondo(checked as boolean)}
            />
            <Label htmlFor="hediondo">Crime Hediondo ou Equiparado</Label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox 
              id="reincidente" 
              checked={reincidente}
              onCheckedChange={(checked) => setReincidente(checked as boolean)}
            />
            <Label htmlFor="reincidente">Reincidente</Label>
          </div>
        </div>

        <div className="p-3 bg-blue-50 rounded-lg text-sm">
          <p><strong>Requisito Temporal:</strong> {getPercentualTexto()}</p>
          <p className="text-muted-foreground mt-1">
            {crimeHediondo 
              ? "Lei 8.072/90 (Crimes Hediondos)" 
              : "Lei 7.210/84 (Lei de Execução Penal)"}
          </p>
        </div>
        
        <Button 
          onClick={handleCalcular} 
          className="w-full"
          disabled={loading}
        >
          {loading ? "Calculando..." : "Calcular Progressão"}
        </Button>
        
        {resultado && (
          <div className="mt-4 p-4 bg-muted rounded-lg space-y-2">
            <h3 className="font-semibold mb-2">Requisito Temporal:</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <p><strong>Pena Total:</strong> {formatMeses(parseFloat(penaTotal))}</p>
                <p><strong>Percentual:</strong> {resultado.percentualCumprido}%</p>
                <p><strong>Tempo Necessário:</strong> {formatMeses(resultado.tempoNecessario)}</p>
              </div>
              <div>
                <p><strong>Data Estimada:</strong> {resultado.dataProgressao}</p>
                <p className="text-green-600 text-xs mt-2">
                  * Ainda necessário requisito subjetivo (bom comportamento)
                </p>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};